import { HeroIcons } from '../../../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import useLocale from '../../../hooks/useLocale'
import Link from '../Link'
import routes from '../../../config/route'
import { getLocaleAppBaseUrl } from '../../../lib/utils/language'
import { getExternalLinkRel } from '../../../lib/utils/seo'
import gtmService, { DestinationType } from '../../../services/gtm.service'

interface FooterLinkItem {
  label: string
  soon?: boolean
  key?: string
}

interface LegalLinkItem {
  label: string
  key?: string
}

interface FooterLinkGroupCopy {
  title: string
  links: FooterLinkItem[]
}

interface FooterLinkGroup extends FooterLinkGroupCopy {
  title: string
  icon: typeof HeroIcons.ChartBarSquareIcon
  links: FooterLinkItem[]
}

type ResolvedFooterLink = { href: string; external: boolean }

const resolveFooterHref = (
  key: string | undefined,
  locale: string,
  lang: 'fr' | 'en',
): ResolvedFooterLink | null => {
  switch (key) {
    case 'signup':
      return { href: getLocaleAppBaseUrl(locale, 'register'), external: false }
    case 'login':
      return { href: getLocaleAppBaseUrl(locale, 'login'), external: false }
    case 'opportunities':
      return {
        href: getLocaleAppBaseUrl(locale, 'properties'),
        external: false,
      }
    case 'whitepaper':
      return {
        href: `/whitepaper/Shelters-White-paper-v1.3-${lang}.pdf`,
        external: true,
      }
    case 'blog':
      return { href: routes.blog, external: false }
    case 'team':
      return { href: routes.team, external: false }
    case 'contact':
      return { href: routes.contact, external: false }
    case 'newsletter':
      return { href: routes.newsletter, external: false }
    default:
      return null
  }
}

const resolveLegalHref = (key: string | undefined): string => {
  switch (key) {
    case 'legalNotices':
      return routes.legalNotices
    case 'privacy':
      return routes.privacyPolicy
    case 'terms':
      return routes.termsOfUse
    default:
      return '#'
  }
}

const getFooterDestinationType = (
  key: string | undefined,
  href: string,
): DestinationType => {
  switch (key) {
    case 'signup':
      return 'app_register'
    case 'login':
      return 'app_login'
    case 'opportunities':
      return 'app_properties'
    case 'whitepaper':
      return 'whitepaper'
    case 'blog':
      return 'blog'
    case 'contact':
      return 'contact'
    case 'newsletter':
      return 'newsletter'
    default:
      return href.startsWith('http') ? 'external' : 'internal'
  }
}

const footerLinkIcons: Record<string, typeof HeroIcons.ChartBarSquareIcon> = {
  app: HeroIcons.ArrowDownTrayIcon,
  opportunities: HeroIcons.SparklesIcon,
  blog: HeroIcons.NewspaperIcon,
  whitepaper: HeroIcons.DocumentTextIcon,
  team: HeroIcons.UserGroupIcon,
  contact: HeroIcons.EnvelopeIcon,
  newsletter: HeroIcons.EnvelopeOpenIcon,
  signup: HeroIcons.UserPlusIcon,
  login: HeroIcons.LockClosedIcon,
}

const FooterTextLink = ({
  link,
  locale,
  lang,
  className,
  leadingIcon: LeadingIcon,
  linkLocation,
  children,
}: {
  link: FooterLinkItem
  locale: string
  lang: 'fr' | 'en'
  className: string
  leadingIcon?: typeof HeroIcons.ChartBarSquareIcon
  linkLocation: string
  children: React.ReactNode
}) => {
  const resolved = link.soon ? null : resolveFooterHref(link.key, locale, lang)

  const trackClick = () => {
    if (!resolved) return

    const destinationType = getFooterDestinationType(link.key, resolved.href)
    gtmService.ctaClicked({
      ctaId: `footer_${link.key || link.label}`,
      ctaLabel: link.label,
      ctaLocation: linkLocation,
      destinationUrl: resolved.href,
      destinationType,
      language: locale,
    })
  }

  const content = (
    <>
      {LeadingIcon && (
        <LeadingIcon className="h-5 w-5 shrink-0 text-sheltersBlue" />
      )}
      {children}
      {resolved?.external && !LeadingIcon && (
        <HeroIcons.ArrowUpRightIcon className="h-3.5 w-3.5 shrink-0 opacity-60" />
      )}
    </>
  )

  if (!resolved) {
    return (
      <span className={className.replace(/\s*hover:underline/g, '')}>
        {content}
      </span>
    )
  }

  if (resolved.external) {
    return (
      <a
        href={resolved.href}
        target="_blank"
        rel={getExternalLinkRel(resolved.href)}
        onClick={trackClick}
        className={className}
      >
        {content}
      </a>
    )
  }

  return (
    <Link href={resolved.href} onClick={trackClick} className={className}>
      {content}
    </Link>
  )
}

const footerGroupIcons = [
  HeroIcons.ChartBarSquareIcon,
  HeroIcons.BookOpenIcon,
  HeroIcons.DocumentTextIcon,
  HeroIcons.UserGroupIcon,
]
const mobileLegalIcons = [
  HeroIcons.DocumentTextIcon,
  HeroIcons.ShieldCheckIcon,
  HeroIcons.ScaleIcon,
  HeroIcons.CircleStackIcon,
]

interface AskAIItem {
  brand: string
  iconSrc: string
  hrefs: {
    fr: string
    en: string
  }
}

const askAIItems: AskAIItem[] = [
  {
    brand: 'ChatGPT',
    iconSrc: '/img/logos/ai/chatgpt-AI.svg',
    hrefs: {
      fr: 'https://chatgpt.com/?q=Comment%20Shelters%20Finance%20permet%20d%27investir%20dans%20l%27immobilier%20%C3%A0%20partir%20de%2010%E2%82%AC%20%3F',
      en: 'https://chatgpt.com/?q=How%20does%20Shelters%20Finance%20make%20it%20possible%20to%20invest%20in%20real%20estate%20from%20%E2%82%AC10%3F',
    },
  },
  {
    brand: 'Claude',
    iconSrc: '/img/logos/ai/claude-AI.svg',
    hrefs: {
      fr: 'https://claude.ai/new?q=Comment%20Shelters%20Finance%20permet%20d%27investir%20dans%20l%27immobilier%20%C3%A0%20partir%20de%2010%E2%82%AC%20%3F',
      en: 'https://claude.ai/new?q=How%20does%20Shelters%20Finance%20make%20it%20possible%20to%20invest%20in%20real%20estate%20from%20%E2%82%AC10%3F',
    },
  },
  {
    brand: 'Gemini',
    iconSrc: '/img/logos/ai/gemini-AI.svg',
    hrefs: {
      fr: 'https://gemini.google.com/guided-learning?query=Comment%20Shelters%20Finance%20permet%20d%27investir%20dans%20l%27immobilier%20%C3%A0%20partir%20de%2010%E2%82%AC%20%3F',
      en: 'https://gemini.google.com/guided-learning?query=How%20does%20Shelters%20Finance%20make%20it%20possible%20to%20invest%20in%20real%20estate%20from%20%E2%82%AC10%3F',
    },
  },
  {
    brand: 'Grok',
    iconSrc: '/img/logos/ai/grok-AI.svg',
    hrefs: {
      fr: 'https://grok.com/?q=Comment%20Shelters%20Finance%20permet%20d%27investir%20dans%20l%27immobilier%20%C3%A0%20partir%20de%2010%E2%82%AC%20%3F',
      en: 'https://grok.com/?q=How%20does%20Shelters%20Finance%20make%20it%20possible%20to%20invest%20in%20real%20estate%20from%20%E2%82%AC10%3F',
    },
  },
  {
    brand: 'Perplexity',
    iconSrc: '/img/logos/ai/perplexity-AI.svg',
    hrefs: {
      fr: 'https://www.perplexity.ai/search?q=Comment%20Shelters%20Finance%20permet%20d%27investir%20dans%20l%27immobilier%20%C3%A0%20partir%20de%2010%E2%82%AC%20%3F',
      en: 'https://www.perplexity.ai/search?q=How%20does%20Shelters%20Finance%20make%20it%20possible%20to%20invest%20in%20real%20estate%20from%20%E2%82%AC10%3F',
    },
  },
]

export default function Footer() {
  const { t } = useTranslation('index')
  const { locale } = useLocale()
  const sectionRef = useRef<HTMLElement | null>(null)
  const hasAnimatedRef = useRef(false)
  const [isInView, setIsInView] = useState(false)
  const [openMobileGroup, setOpenMobileGroup] = useState<string | null>(null)
  const currentLang = locale?.startsWith('fr') ? 'fr' : 'en'
  const footerGroupCopiesResult = t('landing.footer.groups', {
    returnObjects: true,
  })
  const footerGroupCopies = Array.isArray(footerGroupCopiesResult)
    ? (footerGroupCopiesResult as FooterLinkGroupCopy[])
    : []
  const footerGroups: FooterLinkGroup[] = footerGroupCopies.map(
    (group, index) => ({
      ...group,
      icon: footerGroupIcons[index] || HeroIcons.ChartBarSquareIcon,
    }),
  )
  const legalLinksResult = t('landing.footer.legalLinks', {
    returnObjects: true,
  })
  const legalLinks = Array.isArray(legalLinksResult)
    ? (legalLinksResult as LegalLinkItem[])
    : []
  const mobileLegalLinks = legalLinks.map((link, index) => ({
    ...link,
    icon: mobileLegalIcons[index] || HeroIcons.DocumentTextIcon,
  }))
  const activeMobileGroup = openMobileGroup ?? footerGroups[0]?.title

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry.isIntersecting || hasAnimatedRef.current) return

        hasAnimatedRef.current = true
        setIsInView(true)
        observer.disconnect()
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -10% 0px',
      },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden py-16 md:py-24"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-footer bg-cover bg-center"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-28 shadow-[inset_0_34px_46px_rgba(32,45,108,0.16)]"
      />

      <div className="relative mx-auto w-full px-3 md:px-10 lg:px-16 xl:px-24 2xl:px-44">
        <div
          className={`relative mx-auto w-full overflow-hidden rounded-container border border-white/65 bg-[linear-gradient(145deg,rgba(255,255,255,0.74)_0%,rgba(255,255,255,0.56)_44%,rgba(225,233,255,0.64)_100%)] px-6 pb-7 pt-7 shadow-[0_26px_58px_rgba(20,15,72,0.24)] backdrop-blur-2xl backdrop-saturate-150 transition-all duration-700 md:px-10 md:pb-9 md:pt-9 ${
            isInView ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.38)_0%,rgba(255,255,255,0)_52%)]" />
          <div className="pointer-events-none absolute -left-16 -top-20 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(182,196,255,0.33)_0%,transparent_72%)]" />
          <div className="pointer-events-none absolute -bottom-28 -right-20 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(170,152,255,0.3)_0%,transparent_74%)]" />

          <div className="relative z-10">
            <div className="desktop:hidden">
              <div className="flex flex-col items-center text-center">
                <img
                  src="/img/logos/shelters/logo-128x128-min.png"
                  alt="Shelters"
                  className="h-20 w-20 rounded-card shadow-[0_14px_28px_rgba(51,40,154,0.26)]"
                />
                <p className="mt-6 max-w-[560px] text-shelters-lead leading-[1.55] text-[#17213d]">
                  {t('landing.footer.description')}
                </p>
              </div>

              <div className="mt-7 flex items-center justify-center gap-4">
                <a
                  href={process.env.NEXT_PUBLIC_LINKEDIN_PAGE || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    process.env.NEXT_PUBLIC_LINKEDIN_PAGE &&
                    gtmService.outboundClick({
                      linkUrl: process.env.NEXT_PUBLIC_LINKEDIN_PAGE,
                      linkLabel: 'LinkedIn',
                      linkLocation: 'footer_mobile_social',
                    })
                  }
                  className="inline-flex h-12 w-12 items-center justify-center rounded-inner-card border border-[#dfe5fb] bg-white/50 shadow-[0_10px_20px_rgba(51,40,154,0.08)] backdrop-blur-md"
                  aria-label="LinkedIn"
                >
                  <span
                    className="inline-block h-6 w-6 bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] [-webkit-mask-image:url('/img/logos/brands/linkedin.svg')] [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-image:url('/img/logos/brands/linkedin.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
                    aria-hidden
                  />
                </a>
                <a
                  href={process.env.NEXT_PUBLIC_TWITTER_PAGE || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    process.env.NEXT_PUBLIC_TWITTER_PAGE &&
                    gtmService.outboundClick({
                      linkUrl: process.env.NEXT_PUBLIC_TWITTER_PAGE,
                      linkLabel: 'X',
                      linkLocation: 'footer_mobile_social',
                    })
                  }
                  className="inline-flex h-12 w-12 items-center justify-center rounded-inner-card border border-[#dfe5fb] bg-white/50 shadow-[0_10px_20px_rgba(51,40,154,0.08)] backdrop-blur-md"
                  aria-label="X"
                >
                  <span
                    className="inline-block h-6 w-6 bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] [-webkit-mask-image:url('/img/logos/brands/twitter.svg')] [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-image:url('/img/logos/brands/twitter.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
                    aria-hidden
                  />
                </a>
                <a
                  href={process.env.NEXT_PUBLIC_INSTAGRAM_PAGE || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    process.env.NEXT_PUBLIC_INSTAGRAM_PAGE &&
                    gtmService.outboundClick({
                      linkUrl: process.env.NEXT_PUBLIC_INSTAGRAM_PAGE,
                      linkLabel: 'Instagram',
                      linkLocation: 'footer_mobile_social',
                    })
                  }
                  className="inline-flex h-12 w-12 items-center justify-center rounded-inner-card border border-[#dfe5fb] bg-white/50 shadow-[0_10px_20px_rgba(51,40,154,0.08)] backdrop-blur-md"
                  aria-label="Instagram"
                >
                  <span
                    className="inline-block h-6 w-6 bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] [-webkit-mask-image:url('/img/logos/brands/instagram.svg')] [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-image:url('/img/logos/brands/instagram.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
                    aria-hidden
                  />
                </a>
                <a
                  href={process.env.NEXT_PUBLIC_DISCORD_INVITE || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    process.env.NEXT_PUBLIC_DISCORD_INVITE &&
                    gtmService.outboundClick({
                      linkUrl: process.env.NEXT_PUBLIC_DISCORD_INVITE,
                      linkLabel: 'Discord',
                      linkLocation: 'footer_mobile_social',
                    })
                  }
                  className="inline-flex h-12 w-12 items-center justify-center rounded-inner-card border border-[#dfe5fb] bg-white/50 shadow-[0_10px_20px_rgba(51,40,154,0.08)] backdrop-blur-md"
                  aria-label="Discord"
                >
                  <span
                    className="inline-block h-6 w-6 bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] [-webkit-mask-image:url('/img/logos/brands/discord.svg')] [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-image:url('/img/logos/brands/discord.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
                    aria-hidden
                  />
                </a>
              </div>

              <div className="mt-8 border-t border-[#d6def6]">
                {footerGroups.map((group) => {
                  const Icon = group.icon
                  const isOpen = activeMobileGroup === group.title

                  return (
                    <div
                      key={group.title}
                      className="border-b border-[#d6def6]"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenMobileGroup(isOpen ? '' : group.title)
                        }
                        aria-expanded={isOpen}
                        className="flex w-full items-center justify-between gap-4 py-5 text-left"
                      >
                        <span className="flex items-center gap-4">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-inner-card bg-[#edefff] text-sheltersBlue">
                            <Icon className="h-5 w-5" />
                          </span>
                          <span className="text-shelters-lead font-medium text-sheltersBlue">
                            {group.title}
                          </span>
                        </span>
                        <HeroIcons.ChevronDownIcon
                          className={`h-5 w-5 text-[#34426b] transition-transform duration-300 ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      <div
                        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                          isOpen
                            ? 'grid-rows-[1fr] opacity-100'
                            : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <ul className="mb-4 space-y-3 rounded-inner-card bg-white/40 px-4 py-4 backdrop-blur-md">
                            {group.links.map((link) => (
                              <li key={`${group.title}-${link.label}`}>
                                <FooterTextLink
                                  link={link}
                                  locale={locale}
                                  lang={currentLang}
                                  leadingIcon={footerLinkIcons[link.key ?? '']}
                                  linkLocation="footer_mobile_links"
                                  className="flex items-center gap-2 text-shelters-body text-[#202942] hover:underline"
                                >
                                  <span>{link.label}</span>
                                  {link.soon && (
                                    <span className="rounded-full border border-[#bfcaff] bg-[#edf2ff] px-2 py-0.5 text-[0.74rem] leading-none text-[#4a56a0]">
                                      {t('landing.footer.comingSoon')}
                                    </span>
                                  )}
                                </FooterTextLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="mt-6 rounded-inner-card border border-[#dfe5fb] bg-[linear-gradient(135deg,rgba(237,239,255,0.72)_0%,rgba(255,255,255,0.42)_55%,rgba(210,218,255,0.72)_100%)] px-4 py-4 backdrop-blur-md">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] bg-clip-text text-shelters-body font-semibold text-transparent">
                    {t('landing.footer.askAI')}
                  </p>
                  <div className="flex items-center gap-4">
                    {askAIItems.map((item) => (
                      <a
                        key={item.brand}
                        href={item.hrefs[currentLang]}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          gtmService.outboundClick({
                            linkUrl: item.hrefs[currentLang],
                            linkLabel: item.brand,
                            linkLocation: 'footer_mobile_ask_ai',
                          })
                        }
                        aria-label={`${item.brand} ${currentLang.toUpperCase()}`}
                        className="inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5"
                      >
                        <img
                          src={item.iconSrc}
                          alt={item.brand}
                          className="h-6 w-6 object-contain"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 border-t border-[#d6def6] pt-5">
                <div className="grid grid-cols-1 gap-y-4">
                  {mobileLegalLinks.map((link) => {
                    const Icon = link.icon

                    return (
                      <Link
                        key={link.label}
                        href={resolveLegalHref(link.key)}
                        className="flex items-center justify-center gap-3 text-center text-shelters-body text-[#202942] hover:underline"
                      >
                        <Icon className="h-6 w-6 text-[#40507b]" />
                        <span>{link.label}</span>
                      </Link>
                    )
                  })}
                </div>

                <p className="mt-6 border-t border-[#d6def6] pt-5 text-shelters-body leading-[1.55] text-[#202942]/80">
                  {t('landing.footer.disclaimer')}
                </p>
              </div>
            </div>

            <div className="hidden desktop:block">
              <div className="grid gap-9 xl:grid-cols-[200px_1fr]">
                <div className="flex items-start">
                  <img
                    src="/img/logos/shelters/logo-128x128-min.png"
                    alt="Shelters"
                    className="h-16 w-16 rounded-inner-card shadow-[0_10px_20px_rgba(51,40,154,0.26)]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-4">
                  {footerGroups.map((group) => (
                    <div key={group.title}>
                      <p className="text-lg font-medium text-sheltersBlue">
                        {group.title}
                      </p>
                      <ul className="mt-2 space-y-1.5">
                        {group.links.map((link) => (
                          <li key={`${group.title}-${link.label}`}>
                            <FooterTextLink
                              link={link}
                              locale={locale}
                              lang={currentLang}
                              linkLocation="footer_desktop_links"
                              className="inline-flex items-center gap-1.5 text-shelters-body text-[#212741] hover:underline"
                            >
                              {link.label}
                              {link.soon && (
                                <span className="rounded-full border border-[#d4dcfb] bg-[#edf2ff] px-2 py-0.5 text-[0.74rem] leading-none text-[#4a56a0]">
                                  {t('landing.footer.comingSoon')}
                                </span>
                              )}
                            </FooterTextLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10 border-t border-[#d6def6] pt-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                    <div className="flex items-center gap-2">
                      <a
                        href={process.env.NEXT_PUBLIC_LINKEDIN_PAGE || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          process.env.NEXT_PUBLIC_LINKEDIN_PAGE &&
                          gtmService.outboundClick({
                            linkUrl: process.env.NEXT_PUBLIC_LINKEDIN_PAGE,
                            linkLabel: 'LinkedIn',
                            linkLocation: 'footer_desktop_social',
                          })
                        }
                        className="inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5"
                        aria-label="LinkedIn"
                      >
                        <span
                          className="inline-block h-5 w-5 bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] [-webkit-mask-image:url('/img/logos/brands/linkedin.svg')] [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-image:url('/img/logos/brands/linkedin.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
                          aria-hidden
                        />
                      </a>
                      <a
                        href={process.env.NEXT_PUBLIC_TWITTER_PAGE || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          process.env.NEXT_PUBLIC_TWITTER_PAGE &&
                          gtmService.outboundClick({
                            linkUrl: process.env.NEXT_PUBLIC_TWITTER_PAGE,
                            linkLabel: 'X',
                            linkLocation: 'footer_desktop_social',
                          })
                        }
                        className="inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5"
                        aria-label="X"
                      >
                        <span
                          className="inline-block h-5 w-5 bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] [-webkit-mask-image:url('/img/logos/brands/twitter.svg')] [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-image:url('/img/logos/brands/twitter.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
                          aria-hidden
                        />
                      </a>
                      <a
                        href={process.env.NEXT_PUBLIC_INSTAGRAM_PAGE || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          process.env.NEXT_PUBLIC_INSTAGRAM_PAGE &&
                          gtmService.outboundClick({
                            linkUrl: process.env.NEXT_PUBLIC_INSTAGRAM_PAGE,
                            linkLabel: 'Instagram',
                            linkLocation: 'footer_desktop_social',
                          })
                        }
                        className="inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5"
                        aria-label="Instagram"
                      >
                        <span
                          className="inline-block h-5 w-5 bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] [-webkit-mask-image:url('/img/logos/brands/instagram.svg')] [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-image:url('/img/logos/brands/instagram.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
                          aria-hidden
                        />
                      </a>
                      <a
                        href={process.env.NEXT_PUBLIC_DISCORD_INVITE || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          process.env.NEXT_PUBLIC_DISCORD_INVITE &&
                          gtmService.outboundClick({
                            linkUrl: process.env.NEXT_PUBLIC_DISCORD_INVITE,
                            linkLabel: 'Discord',
                            linkLocation: 'footer_desktop_social',
                          })
                        }
                        className="inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5"
                        aria-label="Discord"
                      >
                        <span
                          className="inline-block h-5 w-5 bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] [-webkit-mask-image:url('/img/logos/brands/discord.svg')] [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-image:url('/img/logos/brands/discord.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
                          aria-hidden
                        />
                      </a>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                      {legalLinks.map((link) => (
                        <Link
                          key={link.label}
                          href={resolveLegalHref(link.key)}
                          className="text-shelters-body text-[#303753] hover:underline"
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <p className="bg-[linear-gradient(100deg,#33289a_0%,#5a5de7_48%,#8d6dca_100%)] bg-clip-text text-shelters-body font-medium text-transparent">
                      {t('landing.footer.askAI')}
                    </p>
                    {askAIItems.map((item) => (
                      <a
                        key={item.brand}
                        href={item.hrefs[currentLang]}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        onClick={() =>
                          gtmService.outboundClick({
                            linkUrl: item.hrefs[currentLang],
                            linkLabel: item.brand,
                            linkLocation: 'footer_desktop_ask_ai',
                          })
                        }
                        aria-label={`${item.brand} ${currentLang.toUpperCase()}`}
                        className="inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5"
                      >
                        <img
                          src={item.iconSrc}
                          alt={item.brand}
                          className="h-5 w-5 object-contain"
                        />
                      </a>
                    ))}
                  </div>
                </div>

                <p className="text-[#3b4260]/82 mt-5 text-[0.93rem] leading-[1.45]">
                  {t('landing.footer.fullDisclaimer')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
